import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConversation } from '../../contexts/ConversationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../contexts/ToastContext';
import { MessageList } from './MessageList';
import type { PhaseEvent } from '../../hooks/usePersonaToolProgress';
import * as modelService from '../../services/modelService';
import { ModelSelector } from '../models/ModelSelector';
import { SendToMenu } from './SendToMenu';
import ComposeMenu from './ComposeMenu';

// Auto-fence pasted content above either threshold so a dropped log /
// JSON / code blob doesn't unfurl as running paragraphs in the markdown
// renderer. Numbers picked to feel "obviously big" — a typed paragraph
// is well under both, an accidental log dump trips both.
const PASTE_FENCE_CHAR_THRESHOLD = 500;
const PASTE_FENCE_LINE_THRESHOLD = 5;

interface ConversationInterfaceProps {
  title: string;
  subtitle?: string;
  avatarName?: string;
  avatarSpecialization?: string;
  avatarUrl?: string;
  className?: string;
  sessionId?: string;
  personaId?: string;
  personaName?: string;
  /** Hide the built-in header when parent provides its own */
  hideHeader?: boolean;
  /** Show only the toolbar (model + send-to) without avatar/title/back */
  compactHeader?: boolean;
  /** Status text to show alongside loading dots */
  loadingStatus?: string;
  /** Structured phase-event stream from the persona chat progress hook.
   *  Drives the expandable phase trail above the pending assistant message —
   *  one record per tool/peer/heartbeat transition, so individual terminal
   *  transitions survive even when aggregate views are displayed. */
  phaseEvents?: PhaseEvent[];
}

export const ConversationInterface: React.FC<ConversationInterfaceProps> = ({
  title,
  subtitle,
  avatarName,
  avatarSpecialization,
  avatarUrl,
  className = '',
  sessionId,
  personaId,
  personaName,
  hideHeader = false,
  compactHeader = false,
  loadingStatus,
  phaseEvents,
}) => {
  const { state, sendMessage } = useConversation();
  const { darkMode } = useTheme();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [currentModel, setCurrentModel] = useState<string | null>(null);
  const [availableModels, setAvailableModels] = useState<any[]>([]);
  const [showModelDropdown, setShowModelDropdown] = useState<boolean>(false);
  const [composeText, setComposeText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Cursor-aware insert. Drops `text` at the textarea's caret (or
  // replaces the current selection), keeping any draft the user has
  // already typed intact. The previous behavior — `setComposeText(text)`
  // wholesale — silently ate user-typed content the moment a chip or
  // template fired. rAF callback restores focus + caret position + the
  // auto-resize height after React commits the new value, so the
  // user never has to re-click into the textarea after picking from
  // ComposeMenu.
  const insertAtCursor = useCallback(
    (text: string) => {
      const ta = textareaRef.current;
      if (!ta) {
        setComposeText((prev) => prev + text);
        return;
      }
      const start = ta.selectionStart ?? composeText.length;
      const end = ta.selectionEnd ?? composeText.length;
      const next = composeText.slice(0, start) + text + composeText.slice(end);
      setComposeText(next);
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.focus();
        const pos = start + text.length;
        el.setSelectionRange(pos, pos);
        el.style.height = 'auto';
        el.style.height = Math.min(el.scrollHeight, 120) + 'px';
      });
    },
    [composeText],
  );

  // Big-paste auto-fence. If the pasted blob crosses either threshold AND
  // the caret isn't already inside an open ``` fence, wrap the paste in
  // a fenced code block so the markdown renderer treats it as a code
  // chunk instead of unfurling it. One-tick info toast tells the user
  // what happened so the auto-wrap doesn't feel like ghost editing.
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const pasted = e.clipboardData.getData('text');
      if (!pasted) return;
      const charCount = pasted.length;
      const lineCount = pasted.split('\n').length;
      const isLarge =
        charCount > PASTE_FENCE_CHAR_THRESHOLD ||
        lineCount > PASTE_FENCE_LINE_THRESHOLD;
      if (!isLarge) return;
      const ta = e.currentTarget;
      const before = composeText.slice(0, ta.selectionStart ?? 0);
      const fenceCount = (before.match(/```/g) ?? []).length;
      if (fenceCount % 2 === 1) return; // already inside an open fence
      e.preventDefault();
      const wrapped = `\n\`\`\`\n${pasted.replace(/\n+$/, '')}\n\`\`\`\n`;
      insertAtCursor(wrapped);
      showToast(
        `Pasted ${charCount.toLocaleString()} chars · auto-wrapped as code block`,
        'info',
      );
    },
    [composeText, insertAtCursor, showToast],
  );

  // Fetch the current model being used for this session
  useEffect(() => {
    if (sessionId) {
      const fetchModel = async () => {
        try {
          const modelData = await modelService.getDefaultModel('session', sessionId);
          // Response shape has two variants:
          //   with preference:   { preference: { model_id, ... }, model: { id, ... } }
          //   system default:    { model: { id, ... }, is_system_default: true }
          // Previous code read modelData.model_id (a field that doesn't exist on
          // either shape), so currentModel was always undefined.
          const modelId =
            modelData?.preference?.model_id ??
            modelData?.model?.id ??
            null;
          setCurrentModel(modelId);
        } catch (error) {
          console.error('Failed to fetch model:', error);
          // If we can't fetch the specific model, use the default
          setCurrentModel(null); // Let the backend decide via model preference lookup
        }
      };

      fetchModel();
    }
  }, [sessionId]);

  // Fetch available models
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const models = await modelService.getModels();
        setAvailableModels(models);
      } catch (error) {
        console.error('Failed to fetch models:', error);
      }
    };
    
    fetchModels();
  }, []);

  // Handle model change
  const handleModelChange = async (modelId: string) => {
    if (sessionId) {
      try {
        await modelService.setModelPreference('session', sessionId, modelId);
        setCurrentModel(modelId);
        setShowModelDropdown(false);
      } catch (error) {
        console.error('Failed to change model:', error);
      }
    }
  };


  return (
    <div className={`flex flex-col h-full ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'} ${className}`}>
      {/* Header — full, compact (tools only), or hidden */}
      {!hideHeader && (
        <div className={`px-3 py-2 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} flex justify-between items-center shrink-0`}>
          {!compactHeader && (
            <div className="flex items-center">
              <button
                onClick={() => navigate('/chat/sessions')}
                className={`mr-3 p-2 rounded-full transition-colors ${
                  darkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
                }`}
                aria-label="Back to chats"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </button>
              <div className="flex items-center">
                {avatarName && (
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-medium mr-3 ${
                    darkMode ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {avatarName.charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <h2 className="font-semibold text-lg">{title}</h2>
                  {subtitle && <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{subtitle}</p>}
                </div>
              </div>
            </div>
          )}
          {compactHeader && <div />}
          {/* Toolbar: Send To (model picker moved to footer) */}
          {!compactHeader && (
            <div className="flex items-center space-x-2">
              {sessionId && personaId && personaName && (
                <SendToMenu
                  sessionId={sessionId}
                  personaId={personaId}
                  personaName={personaName}
                />
              )}
            </div>
          )}
        </div>
      )}

      <MessageList
        messages={state.messages}
        isLoading={state.status === 'loading'}
        loadingStatus={loadingStatus}
        phaseEvents={phaseEvents}
        currentModel={currentModel}
        sessionId={sessionId}
        personaId={personaId}
        personaName={personaName}
      />

      {/* ── Single-decker Compose Bar ── */}
      <div className={`px-3 py-2 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} shrink-0`}>
        <div className="flex items-center gap-2">
          {/* Tools: Model + SendTo + Compose */}
          <ModelSelector
            entityType="session"
            entityId={sessionId || ''}
            onModelSelect={handleModelChange}
            showCosts={false}
            compact={true}
            className="z-20"
            placement="top-left"
            showDropdown={showModelDropdown}
            setShowDropdown={setShowModelDropdown}
          />
          {sessionId && personaId && personaName && (
            <SendToMenu
              sessionId={sessionId}
              personaId={personaId}
              personaName={personaName}
            />
          )}
          <ComposeMenu
            personaId={personaId}
            personaName={personaName}
            sessionId={sessionId}
            onInsert={insertAtCursor}
          />

          {/* Input + Send. Textarea is NOT disabled while loading — the
              user can compose the next message while the assistant
              works. Send is still gated on idle so we don't double-fire.
              Form-submit + Enter both bail silently when loading. */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const msg = (composeText || '').trim();
              if (!msg || state.status === 'loading') return;
              sendMessage(msg);
              setComposeText('');
              if (textareaRef.current) textareaRef.current.style.height = 'auto';
            }}
            className="flex flex-1 gap-0 items-end"
          >
            <textarea
              ref={textareaRef}
              value={composeText}
              onChange={(e) => {
                setComposeText(e.target.value);
                // Auto-resize on every keystroke. Reset-to-auto first
                // so deleting back down to a single line contracts the
                // height instead of holding the previously-grown row.
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  const msg = (composeText || '').trim();
                  if (!msg || state.status === 'loading') return;
                  sendMessage(msg);
                  setComposeText('');
                  (e.target as HTMLTextAreaElement).style.height = 'auto';
                }
              }}
              onPaste={handlePaste}
              placeholder={
                personaName
                  ? `Message ${personaName.replace(/_/g, ' ')}…`
                  : 'Type your message…'
              }
              rows={1}
              className={`flex-1 px-3 py-1.5 rounded-l-lg text-sm border focus:outline-none focus:ring-1 focus:ring-emerald-500 resize-none overflow-hidden transition-shadow ${
                darkMode
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
            />
            <button
              type="submit"
              disabled={state.status === 'loading' || !composeText?.trim()}
              className={`px-4 py-1.5 rounded-r-lg text-sm font-medium transition-colors self-stretch ${
                darkMode ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-emerald-500 hover:bg-emerald-600'
              } text-white ${state.status === 'loading' || !composeText?.trim() ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Send
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
